import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';
import { categories as categoryList } from '@/mocks/categories';

const cardColors = [
  { bg: '#f5e6e0', tag: '#e8c4b8' },
  { bg: '#e0ece4', tag: '#b8d4c0' },
  { bg: '#e8e5e0', tag: '#d0cbc4' },
  { bg: '#e8dfd5', tag: '#d4c4b0' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const catInfo = categoryList.find((c) => c.slug === slug) || {
    slug: slug || '',
    name: '全部漫画',
    icon: 'ri-book-open-line',
    description: '浏览所有漫画作品',
  };
  const filteredComics = slug
    ? comics.filter((c) => c.category === slug)
    : comics;

  const featuredComics = filteredComics.filter((c) => c.isHot).slice(0, 3);
  const remainingComics = filteredComics.filter((c) => !c.isHot);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `酷漫屋 - ${catInfo.name}漫画`,
    description: `酷漫屋${catInfo.name}专区，${catInfo.description}。海量精选，高清画质，永久免费。`,
    url: `http://lst56.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '酷漫屋',
      url: 'http://lst56.com/',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://lst56.com/' },
      { '@type': 'ListItem', position: 2, name: `${catInfo.name}漫画` },
    ],
  };

  const pageSlug = slug || 'all';

  return (
    <>
      <SEOHead
        title={`酷漫屋 - ${catInfo.name}漫画专区 | 下拉式免费漫画`}
        description={`酷漫屋${catInfo.name}专区，${catInfo.description}。下拉式阅读体验，高清画质，每日更新，永久免费。`}
        keywords={`酷漫屋,${catInfo.name},免费漫画,下拉式漫画,韩国漫画`}
        canonicalUrl={`http://lst56.com/category/${pageSlug}`}
        jsonLd={categoryJsonLd}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          {/* Hero Banner */}
          <section className="relative w-full h-[340px] md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20webtoon%20style%20illustration%20of%20a%20reading%20scene%20with%20cozy%20warm%20lighting%2C%20soft%20pastel%20color%20palette%2C%20bookshelf%20background%2C%20artistic%20atmosphere%2C%20gentle%20ambient%20light%2C%20Korean%20manhwa%20aesthetic%2C%20clean%20composition%2C%20cinematic%20wide%20shot%2C%20inviting%20warm%20tones&width=1600&height=500&seq=category-hero-banner&orientation=landscape"
              alt="漫画专区"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="relative z-10 w-full h-full flex items-center px-6 md:px-12 lg:px-20">
              <div className="flex flex-col gap-4">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: "var(--font-heading)" }}>
                  {catInfo.name}漫画
                </h1>
                <p className="text-sm md:text-base text-white/70 max-w-md">
                  {catInfo.description}
                </p>
              </div>
            </div>
          </section>

          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类', href: '/category/hanman' },
              { label: `${catInfo.name}漫画` },
            ]}
          />

          {/* Category tabs */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex gap-2 overflow-x-auto pb-2 flex-wrap">
              {categoryList.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Featured comics (3 irregular grid) */}
          {featuredComics.length >= 3 && (
            <section className="py-8 md:py-12 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                  编辑<span className="text-accent-500">精选</span>
                </h2>

                <div className="flex flex-col lg:flex-row gap-4 md:gap-5">
                  {/* Left: Main */}
                  <Link
                    to={`/article/${featuredComics[0].slug}`}
                    className="relative lg:w-1/2 h-[380px] md:h-[440px] rounded-xl overflow-hidden group cursor-pointer"
                  >
                    <img
                      src={featuredComics[0].cover}
                      alt={featuredComics[0].title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{featuredComics[0].title}</h3>
                      <div className="flex items-center gap-3 text-white/70 text-sm">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                          {featuredComics[0].rating}
                        </span>
                        <span>{featuredComics[0].views} 阅读</span>
                      </div>
                    </div>
                  </Link>

                  {/* Right: Two */}
                  <div className="lg:w-1/2 flex flex-col gap-4 md:gap-5">
                    {featuredComics.slice(1).map((comic) => (
                      <Link
                        key={comic.id}
                        to={`/article/${comic.slug}`}
                        className="relative h-[180px] md:h-[210px] rounded-xl overflow-hidden group cursor-pointer"
                      >
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="text-lg font-bold text-white">{comic.title}</h3>
                          <div className="flex items-center gap-3 text-white/60 text-xs mt-1">
                            <span className="flex items-center gap-1">
                              <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                              {comic.rating}
                            </span>
                            <span>{comic.views}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* All comics grid */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                全部<span className="text-accent-500">{catInfo.name}</span>漫画
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {remainingComics.map((comic, index) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.slug}`}
                    className="group cursor-pointer"
                  >
                    <div
                      className="relative rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]"
                      style={{ backgroundColor: cardColors[index % 4].bg }}
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </div>
                    <div className="mt-3 px-1">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 truncate">{comic.title}</h3>
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center gap-1.5">
                          <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                          <span className="text-xs text-foreground-600">{comic.rating}</span>
                        </div>
                        <span className="text-xs text-foreground-400">{comic.status}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {remainingComics.length === 0 && (
                <p className="text-center text-foreground-400 py-12">暂无更多漫画，请查看其他分类</p>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}